import { uniq } from "lodash";
import { useEffect, useMemo, type FC } from "react";
import styled from "styled-components";

import { useGetAggregatedEntityIds } from "@ds/hooks/use-get-aggregated-entity-ids";
import { useDeepRootSelector, useRootDispatch, useRootSelector } from "@ds/hooks";

import { EntityTypeName, isAggregatedEntity } from "@ds/model/entity-model";

import { getDeploymentStatusIndicatorWithNameAndLink } from "@ds/modules/deployments/components/common/status-indicator";
import { selectDeploymentsByVersions, selectDeploymentsIsLoading } from "@ds/modules/deployments/redux/selectors";
import { deploymentsActions } from "@ds/modules/deployments/redux/slice";

import { DetailsCard, DetailsCardRow } from "@ds/components/details-view";
import { MultipleValuesLabelWithLink, UnassignedLabel } from "@ds/components/forms/labels/common";
import { SpinnerIcon } from "@ds/components/loaders/loaders";
import { Loading } from "@ds/components/loaders/loading";
import { formatAbsoluteDate } from "@ds/utils/date";
import { composeDetailsRouteByEntityIds } from "@ds/utils/router";

import { selectPlayersByIds } from "../../redux/selectors";

const StyledSpinnerIcon = styled(SpinnerIcon)`
  width: 15px;
`;

export const PlayerDetailsDeployedContentCard: FC<{ player: Player }> = ({ player }) => {
  const aggregatedPlayerIds = useGetAggregatedEntityIds(player);
  const aggregatedPlayers = useDeepRootSelector(state => selectPlayersByIds(state, aggregatedPlayerIds));

  const contentVersions = useMemo(
    () => uniq(aggregatedPlayers.map(({ content_version }) => content_version).filter((v): v is number => !!v)),
    [aggregatedPlayers],
  );

  const contentDeployments = useDeepRootSelector(state => selectDeploymentsByVersions(state, contentVersions));

  const isLoading = useRootSelector(
    state => selectDeploymentsIsLoading(state) || (!!contentVersions.length && !contentDeployments.length),
  );

  const dispatch = useRootDispatch();
  useEffect(() => {
    contentVersions.forEach(version => {
      dispatch(deploymentsActions.fetchDeployments({ version, type: "content" }));
    });
  }, [dispatch, contentVersions]);

  const detailsBody = useMemo(() => {
    if (isLoading) {
      return null;
    }

    if (isAggregatedEntity(player) && contentDeployments.length > 1) {
      return (
        <MultipleValuesLabelWithLink
          to={composeDetailsRouteByEntityIds(
            contentDeployments.map(({ id }) => id),
            EntityTypeName.DEPLOYMENT,
          )}
        />
      );
    } else if (!contentDeployments[0]) {
      return <UnassignedLabel text="No content has been deployed" />;
    }

    return (
      <>
        <DetailsCardRow label="Deployment">
          {!contentDeployments[0] && <StyledSpinnerIcon />}
          {contentDeployments[0] && getDeploymentStatusIndicatorWithNameAndLink(contentDeployments[0])}
        </DetailsCardRow>
        <DetailsCardRow label="Version">{contentDeployments[0].version}</DetailsCardRow>
        <DetailsCardRow label="When">{formatAbsoluteDate(contentDeployments[0].ended_at)}</DetailsCardRow>
      </>
    );
  }, [isLoading, player, contentDeployments]);

  return (
    <DetailsCard title="Deployed Content" icon="box">
      <Loading isLoading={isLoading}>{detailsBody}</Loading>
    </DetailsCard>
  );
};
